import {
  Container,
  Stack,
  Text,
  TextInput,
  Notification,
  Button,
  Center,
  Title,
  Flex,
  Loader,
} from "@mantine/core";
import { IconMail } from "@tabler/icons-react";
import { useState } from "react";
import SmartWizLogo from "../../components/Logos/SmartWizLogo";
import { sendResetPasswordEmail } from "../../utils/api";
import { useBranding } from "../LoginManagement/hooks/useBranding";
import { useParams } from "react-router-dom";
import AuthLogo from "@/components/WhiteLabel/AuthLogo";
import BrandedAnchor from "@/components/WhiteLabel/BrandedAnchor";

const ForgotPassword = () => {
  // From URL path when on /:businessSlug/forgotpassword (e.g. {domain}/acme/forgotpassword)
  const { businessSlug } = useParams();
  const [email, setEmail] = useState("");
  const [notification, setNotification] = useState<{
    title: string;
    message: string;
    color: string;
    loading?: boolean;
  } | null>(null);
  const {
    businessInfo,
    loading: businessLoading,
    error: businessError,
  } = useBranding();

  const submit = async (e: any) => {
    e.preventDefault();
    setNotification({
      title: "Sending Email",
      message: "Please wait while we send you a password reset email.",
      color: "blue",
      loading: true,
    });
    const normalizedEmail = email.toLowerCase(); // Normalize the email to lowercase
    const response = await sendResetPasswordEmail(
      normalizedEmail,
      businessSlug ?? undefined
    );
    if (response?.status === 200) {
      setNotification({
        title: "Password Reset Email Sent Successfully",
        message:
          "Please check your email for instructions to reset your password.",
        color: "teal",
      });
    } else {
      if ((response as any).response?.data?.message === "Email is required") {
        setNotification({
          title: "Error",
          message: "Email is required",
          color: "red",
        });
      } else {
        // Security: Always show success message regardless of user existence
        // This prevents user enumeration attacks
        setNotification({
          title: "Password Reset Email Sent Successfully",
          message:
            "Please check your email for instructions to reset your password.",
          color: "green",
        });
      }
    }
  };

  if (businessError) {
    return (
      <Container className="auth-form-container" size="sm">
        <Text c="red" size="lg" ta="center">
          {businessError}
        </Text>
      </Container>
    );
  }

  if (businessLoading) {
    return (
      <Container
        className=""
        size="sm"
        style={{ textAlign: "center", height: "100%" }}
      >
        <Center style={{ height: "100vh" }}>
          <SmartWizLogo /> <Loader size="md" color="blue" ml={10} />
        </Center>
      </Container>
    );
  }
  return (
    <Center w="100vw" h="100vh">
      <Container className="" size="xs" pb={100}>
        <form onSubmit={submit}>
          <Center w="100%" mb={24}>
            <AuthLogo
              logoUrl={businessInfo?.logoUrl}
              businessSlug={businessSlug}
              primaryColor={businessInfo?.theme?.primaryColor}
              size={72}
              isSmall={true}
              alt="Business Logo"
            />
          </Center>
          <Title order={1} ta="center">
            Change Account Password
          </Title>
          <Text
            className="sign-up forgot-text"
            size="md"
            ta="center"
            fw={500}
            mb={24}
          >
            Don't worry, it happens. We will email instructions to reset your
            password.
          </Text>
          <Stack
            className="stack"
            gap="md"
            align="stretch"
            px={36}
            justify="center"
          >
            <TextInput
              leftSection={<IconMail color="#A3A9B0" />}
              placeholder="Enter your account email"
              name="email"
              radius="md"
              type="text"
              value={email}
              required
              data-cy="accountEmail"
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button
              className="sign-in"
              type="submit"
              color={businessInfo?.theme?.primaryColor || "blue"}
              radius="md"
            >
              Change Password
            </Button>
            <BrandedAnchor
              className="link"
              businessSlug={businessSlug}
              businessInfo={businessInfo}
              label="Back to Login"
              fw={600}
            />
            {notification && (
              <Notification
                title={notification.title}
                color={notification.color}
                onClose={() => setNotification(null)}
                withCloseButton
                loading={notification.loading}
              >
                {notification.message}
              </Notification>
            )}
            <Flex justify="center" align="center" gap="xs">
              Powered by <SmartWizLogo isLoginPage={true} />
            </Flex>
          </Stack>
        </form>
      </Container>
    </Center>
  );
};

export default ForgotPassword;
